import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { quoteItemsApi, useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { selectLocale } from '@/app/slices/appSlice'
import {
    selectCurrencyCode,
    selectOrganisationId,
    selectPaidFeatures,
    selectUseImperialUnits,
    selectUseTubeV2,
} from '@/app/slices/organisationSlice'
import { selectIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import { useNumberFormatter, useToolBoxTreatments } from '@/common/hooks'
import { useQuoteItemPropertyOptions } from '@/common/hooks/useQuoteItemPropertyOptions'
import { useUpdateQuoteItem } from '@/common/hooks/useUpdateQuoteItem'
import { getCurrencyFormat, getPartDimensions } from '@/common/utils'
import {
    getPartDimensionsDisplay,
    getRecordDimensionsDisplay,
    getThicknessOptionValue,
    parseProfileDimensions,
} from '@/common/utils/SheetUtils/SheetUtils'
import ItemNotesModal from '@/features/quotes/components/common/ItemNotesModal'

import {
    ConsumptionModeSelector,
    CustomerSuppliedMaterialToggle,
    CuttingTechnologySelector,
    GrainDirectionSelector,
    MaterialSelector,
    ProfileControls,
    QuantityInput,
    SettingsHeader,
    ThicknessAndSheetControls,
} from './ItemSettingsComponents'

const classes = {
    ItemDetails_Settings: {
        width: '380px',
        gap: '12px',
    },
}

const ItemSettings = ({ quoteItemId }) => {
    const { quoteId } = useParams()
    const dispatch = useDispatch()

    const { showGrainDirection } = useToolBoxTreatments()

    const { n } = useNumberFormatter()

    const organisationId = useSelector(selectOrganisationId)
    const showTubeOptions = useSelector(selectUseTubeV2)
    const paidFeatures = useSelector(selectPaidFeatures)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const { handleUpdateQuoteItem, markItemPending } = useUpdateQuoteItem()

    const quoteItemProfile = quoteItem?.profile
    const quoteItemDimensions = useMemo(() => {
        return getPartDimensionsDisplay(quoteItem, n)
    }, [n, quoteItem])

    const quoteItemSheetProfile = useMemo(() => {
        if (!showTubeOptions || !quoteItem?.sheetId) return null

        return quoteItem?.sheet?.profile || null
    }, [quoteItem?.sheetId, showTubeOptions, quoteItem?.sheet])

    const quoteItemSheetDimensions = useMemo(() => {
        if (!showTubeOptions || !quoteItem?.sheetId) return null

        return getRecordDimensionsDisplay(quoteItem?.sheet, n)
    }, [quoteItem?.sheetId, showTubeOptions, n, quoteItem?.sheet])

    // UI-only state (not synced from quoteItem)
    const [openNotesModal, setOpenNotesModal] = useState(false)
    const [quantityError, setQuantityError] = useState(false)

    const orgHasCustomerSuppliedMaterial = paidFeatures?.hasCustomerSuppliedMaterial
    const orgHasMaterialConsumptionMode = paidFeatures?.hasMaterialConsumptionModeOptions

    // Ref to track profile dimensions for auto-selection logic
    const prevProfileDimensionsRef = useRef(quoteItemSheetDimensions || quoteItemDimensions || '')

    const materialConsumptionModeEnabled = useMemo(
        () => quoteItem?.materialId && orgHasMaterialConsumptionMode,
        [quoteItem?.materialId, orgHasMaterialConsumptionMode]
    )

    const partHasNotes = useMemo(() => {
        if (!quoteItem) return false
        const { customerNotes, privateNotes, vendorNotes } = quoteItem
        return Boolean(customerNotes) || Boolean(vendorNotes) || Boolean(privateNotes)
    }, [quoteItem])

    const partDimensions = getPartDimensions(quoteItem)
    const units = useImperialUnits ? 'in' : 'mm'
    const mode = quoteItem && 'profile' in quoteItem ? 'rotary' : 'flat'

    const isMaterialUnavailable = !quoteItem?.materialId || !quoteItem?.thickness
    const isRotaryPart = quoteItem && 'profile' in quoteItem

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const currencyFormat = useMemo(() => getCurrencyFormat(currencyCode, locale), [currencyCode, locale])

    const {
        consumptionModeOptions,
        cuttingTechOptions,
        findMatchingProfileDimension,
        grainDirectionOptions,
        materialOptions,
        profileDimensions,
        profileOptions,
        sheetOptions,
        thicknessOptions,
    } = useQuoteItemPropertyOptions({
        cuttingTechnologyId: quoteItem?.cuttingTechnologyId || '',
        materialId: quoteItem?.materialId || '',
        thickness: quoteItem?.thickness || '',
        webThickness: quoteItem?.webThickness || null,
        profileValue: quoteItemSheetProfile || quoteItemProfile || '',
        profileDimensionsValue: quoteItemSheetDimensions || quoteItemDimensions || '',
        mode,
        partDimensions,
    })

    // QM-3717: composite (webThickness, thickness) value for the thickness Select — see
    // getThicknessOptionValue. A no-op string(thickness) for every profile but IBeam.
    const thicknessSelectValue = useMemo(
        () =>
            getThicknessOptionValue({
                profile: quoteItemSheetProfile || quoteItemProfile,
                thickness: quoteItem?.thickness,
                webThickness: quoteItem?.webThickness,
            }),
        [quoteItemSheetProfile, quoteItemProfile, quoteItem?.thickness, quoteItem?.webThickness]
    )

    const selectedSheet = useMemo(() => {
        if (!quoteItem?.sheetId) return null
        return sheetOptions?.find((option) => option.sheetId === quoteItem?.sheetId) || {}
    }, [quoteItem?.sheetId, sheetOptions])

    // Memoize sheetOptions to prevent unnecessary effect triggers
    const memoizedSheetOptions = useMemo(() => {
        if (!sheetOptions?.length) return []
        return sheetOptions
    }, [sheetOptions])

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 800)

    const disableNotesButton = false
    const showNotesModal = useCallback(() => {
        setOpenNotesModal(true)
    }, [])
    const hideNotesModal = useCallback(() => {
        setOpenNotesModal(false)
    }, [])

    const handleCuttingTechChange = useCallback(
        async (event) => {
            const value = event?.target?.value
            if (!value || quoteItem?.cuttingTechnologyId === value) return

            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({
                ...quoteItem,
                cuttingTechnologyId: value,
                materialId: '',
                thickness: '',
                sheetId: '',
                sheet: null,
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
                webThickness: '',
            })
        },
        [markItemPending, quoteItemId, quoteItem, handleUpdateQuoteItem]
    )

    const handleMaterialChange = useCallback(
        async (event) => {
            const value = event?.target?.value
            if (!value || quoteItem?.materialId === value) return

            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({
                ...quoteItem,
                materialId: value,
                thickness: '',
                sheetId: '',
                sheet: null,
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
                webThickness: '',
            })
        },
        [markItemPending, quoteItemId, quoteItem, handleUpdateQuoteItem]
    )

    const handleProfileChange = useCallback(
        (event) => {
            const value = event?.target?.value
            const currentProfile = quoteItemSheetProfile || quoteItemProfile || ''
            if (!value || currentProfile === value) return

            markItemPending(quoteItemId)
            // Reset ref to allow auto-selection of dimensions
            prevProfileDimensionsRef.current = ''

            // Immediate cache update for responsive UI - clear dependent values
            // @ts-ignore
            dispatch(
                // @ts-ignore
                quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                    const item = draft.find((i) => i.id === quoteItemId)
                    if (item) {
                        item.profile = value
                        item.thickness = ''
                        item.sheetId = ''
                        item.sheet = null
                        item.materialConsumptionMode = ''
                        item.isCustomerSuppliedMaterial = false
                        item.webThickness = ''
                    }
                })
            )
        },
        [dispatch, markItemPending, quoteItemId, quoteItemSheetProfile, quoteItemProfile, organisationId, quoteId]
    )

    const handleProfileDimensionsChange = useCallback(
        (event) => {
            const value = event?.target?.value
            const currentDimensions = quoteItemSheetDimensions || quoteItemDimensions || ''
            if (!value || currentDimensions === value) return

            // Validate that the selected dimension is in available options
            const validValue = profileDimensions?.includes(value) ? value : profileDimensions?.[0] || ''
            if (!validValue) return

            markItemPending(quoteItemId)
            // Update ref for auto-selection tracking
            prevProfileDimensionsRef.current = validValue

            // Immediate cache update for responsive UI - clear dependent values
            dispatch(
                // @ts-ignore
                quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                    const item = draft.find((i) => i.id === quoteItemId)
                    if (item) {
                        Object.assign(item, parseProfileDimensions(item.profile, validValue))
                        item.thickness = ''
                        item.sheetId = ''
                        item.sheet = null
                        item.materialConsumptionMode = ''
                        item.isCustomerSuppliedMaterial = false
                        item.webThickness = ''
                    }
                })
            )
        },
        [
            dispatch,
            markItemPending,
            quoteItemId,
            quoteItemSheetDimensions,
            quoteItemDimensions,
            profileDimensions,
            organisationId,
            quoteId,
        ]
    )

    const handleThicknessChange = useCallback(
        (event) => {
            const value = event?.target?.value
            if (!value || value === thicknessSelectValue) return

            // Resolve the composite Select value back to its (thickness, webThickness) pair —
            // event.target.value must never be written to item.thickness directly (QM-3717).
            const option = thicknessOptions?.find((o) => o.value === value)
            if (!option) return

            markItemPending(quoteItemId)
            // Immediate cache update for responsive UI - clear dependent values
            dispatch(
                // @ts-ignore
                quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                    const item = draft.find((i) => i.id === quoteItemId)
                    if (item) {
                        item.thickness = option.thickness
                        item.sheetId = ''
                        item.sheet = null
                        item.materialConsumptionMode = ''
                        item.isCustomerSuppliedMaterial = false
                        item.webThickness = option.webThickness ?? ''
                    }
                })
            )
        },
        [dispatch, markItemPending, quoteItemId, thicknessSelectValue, thicknessOptions, organisationId, quoteId]
    )

    const handleSheetChange = useCallback(
        async (event) => {
            const value = event?.target?.value
            if (!value || quoteItem?.sheetId === value) return

            markItemPending(quoteItemId)
            const selectedSheetOption = sheetOptions?.find((option) => option.sheetId === value) || {}
            const { defaultMaterialConsumptionMode, isSoldByOrganisation, webThickness } = selectedSheetOption

            await handleUpdateQuoteItem({
                ...quoteItem,
                sheetId: value,
                sheet: selectedSheetOption,
                materialConsumptionMode: defaultMaterialConsumptionMode || '',
                isCustomerSuppliedMaterial: !isSoldByOrganisation,
                webThickness: webThickness || '',
            })
        },
        [markItemPending, quoteItemId, quoteItem, sheetOptions, handleUpdateQuoteItem]
    )

    const handleGrainDirectionChange = useCallback(
        async (event) => {
            const value = event?.target?.value
            if (!value || quoteItem?.grainDirection === value) return

            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({
                ...quoteItem,
                grainDirection: value,
            })
        },
        [markItemPending, quoteItemId, quoteItem, handleUpdateQuoteItem]
    )

    const handleConsumptionModeChange = useCallback(
        async (event) => {
            const value = event?.target?.value
            if (!value || quoteItem?.materialConsumptionMode === value) return

            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({
                ...quoteItem,
                materialConsumptionMode: value,
            })
        },
        [markItemPending, quoteItemId, quoteItem, handleUpdateQuoteItem]
    )

    const handleQuantityChange = useCallback(
        (values) => {
            const { floatValue } = values

            markItemPending(quoteItemId)
            debounceHandleUpdateQuoteItem.cancel()
            setQuantityError(!floatValue || isNaN(floatValue) || floatValue <= 0)

            // Immediate cache update for responsive UI
            dispatch(
                // @ts-ignore
                quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                    const item = draft.find((i) => i.id === quoteItemId)
                    if (item) {
                        item.quantity = floatValue
                    }
                })
            )

            if (!isNaN(floatValue) && floatValue >= 1) {
                debounceHandleUpdateQuoteItem({
                    ...quoteItem,
                    quantity: floatValue,
                })
            }
        },
        [dispatch, markItemPending, quoteItemId, debounceHandleUpdateQuoteItem, quoteItem, organisationId, quoteId]
    )

    const handleIsCustomerSuppliedMaterialChange = useCallback(
        async (event) => {
            const value = event?.target?.checked

            markItemPending(quoteItemId)
            await handleUpdateQuoteItem({
                ...quoteItem,
                isCustomerSuppliedMaterial: value,
            })
        },
        [markItemPending, quoteItemId, quoteItem, handleUpdateQuoteItem]
    )

    const loadDefaultSheetOption = useCallback(async () => {
        // Auto-select first sheet when thickness is set but no sheet is selected
        if (!sheetOptions?.length || !quoteItem?.thickness || quoteItem?.sheetId) return

        const firstSheet = sheetOptions[0]
        const { defaultMaterialConsumptionMode, isSoldByOrganisation, sheetId, webThickness } = firstSheet

        // Skip if already set to this sheet
        if (quoteItem.sheetId === sheetId) return

        // Check if update is needed
        if (
            quoteItem.sheetId !== sheetId ||
            quoteItem.materialConsumptionMode !== defaultMaterialConsumptionMode ||
            quoteItem.isCustomerSuppliedMaterial !== !isSoldByOrganisation
        ) {
            markItemPending(quoteItem.id)
            await handleUpdateQuoteItem({
                ...quoteItem,
                sheetId: sheetId,
                sheet: firstSheet,
                materialConsumptionMode: defaultMaterialConsumptionMode,
                isCustomerSuppliedMaterial: !isSoldByOrganisation,
                webThickness: webThickness || '',
            })
        }
    }, [sheetOptions, quoteItem, markItemPending, handleUpdateQuoteItem])

    // Auto-select first sheet when thickness is set but no sheet is selected
    useEffect(() => {
        loadDefaultSheetOption()
    }, [memoizedSheetOptions, loadDefaultSheetOption])

    // Auto-select profile when material changes for rotary parts
    useEffect(() => {
        if (!showTubeOptions || !quoteItemProfile || !profileOptions?.length) return

        const currentProfile = quoteItemSheetProfile || quoteItem?.profile || ''
        // Only auto-select if profile is empty and part's profile is in options
        if (!currentProfile && profileOptions.includes(quoteItemProfile)) {
            // Update cache directly instead of local state
            dispatch(
                // @ts-ignore
                quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                    const item = draft.find((i) => i.id === quoteItemId)
                    if (item) {
                        item.profile = quoteItemProfile
                    }
                })
            )
        }
    }, [
        showTubeOptions,
        quoteItemProfile,
        profileOptions,
        quoteItemSheetProfile,
        quoteItem?.profile,
        dispatch,
        organisationId,
        quoteId,
        quoteItemId,
    ])

    // Auto-select profile dimensions when profile changes for rotary parts
    useEffect(() => {
        if (!showTubeOptions || !quoteItemProfile || !quoteItemDimensions) return

        const currentProfile = quoteItemSheetProfile || quoteItem?.profile || ''
        // Wait for profile to be selected before auto-selecting dimensions
        if (!currentProfile) return

        if (!profileDimensions?.length) {
            if (prevProfileDimensionsRef.current) {
                prevProfileDimensionsRef.current = ''
            }
            return
        }

        // Skip if current dimensions are still valid
        if (prevProfileDimensionsRef.current && profileDimensions.includes(prevProfileDimensionsRef.current)) {
            return
        }

        const matchingDimension = findMatchingProfileDimension(quoteItemDimensions, profileDimensions)
        const newValue = matchingDimension && profileDimensions.includes(matchingDimension) ? matchingDimension : ''

        if (newValue !== prevProfileDimensionsRef.current) {
            prevProfileDimensionsRef.current = newValue
            // Update cache directly instead of local state
            if (newValue) {
                dispatch(
                    // @ts-ignore
                    quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                        const item = draft.find((i) => i.id === quoteItemId)
                        if (item) {
                            Object.assign(item, parseProfileDimensions(item.profile, newValue))
                        }
                    })
                )
            }
        }
    }, [
        quoteItem?.profile,
        quoteItemSheetProfile,
        profileDimensions,
        findMatchingProfileDimension,
        quoteItemDimensions,
        showTubeOptions,
        quoteItemProfile,
        dispatch,
        organisationId,
        quoteId,
        quoteItemId,
    ])

    return (
        <Box
            key={quoteItemId}
            sx={[
                {
                    alignItems: 'flex-start',
                    display: 'flex',
                    flex: '0 0 auto',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                },
                classes.ItemDetails_Settings,
            ]}
        >
            <SettingsHeader
                disabled={disableNotesButton}
                partHasNotes={partHasNotes}
                onNotesClick={showNotesModal}
            />

            <QuantityInput
                // @ts-ignore
                currencyFormat={currencyFormat}
                disabled={isCalculatingQuote}
                error={quantityError}
                value={quoteItem?.quantity ?? 1}
                onValueChange={handleQuantityChange}
            />

            <CuttingTechnologySelector
                disabled={!cuttingTechOptions?.length || isPartFromLibrary || isCalculatingQuote}
                isPartFromLibrary={isPartFromLibrary}
                options={cuttingTechOptions}
                value={quoteItem?.cuttingTechnologyId || ''}
                onChange={handleCuttingTechChange}
            />

            <MaterialSelector
                disabled={
                    !quoteItem?.cuttingTechnologyId ||
                    !materialOptions?.length ||
                    isPartFromLibrary ||
                    isCalculatingQuote
                }
                isMaterialUnavailable={isMaterialUnavailable}
                isPartFromLibrary={isPartFromLibrary}
                options={materialOptions}
                value={quoteItem?.materialId || ''}
                onChange={handleMaterialChange}
            />

            {mode === 'rotary' && showTubeOptions ? (
                <ProfileControls
                    dimensionsDisabled={
                        !(quoteItemSheetProfile || quoteItem?.profile) ||
                        !profileDimensions?.length ||
                        isPartFromLibrary ||
                        isCalculatingQuote
                    }
                    dimensionsOptions={profileDimensions}
                    dimensionsValue={quoteItemSheetDimensions || quoteItemDimensions || ''}
                    isMaterialUnavailable={isMaterialUnavailable}
                    isPartFromLibrary={isPartFromLibrary}
                    profileDisabled={
                        !quoteItem?.cuttingTechnologyId ||
                        !quoteItem?.materialId ||
                        !profileOptions?.length ||
                        isPartFromLibrary ||
                        isCalculatingQuote
                    }
                    profileOptions={profileOptions}
                    profileValue={quoteItemSheetProfile || quoteItem?.profile || ''}
                    units={units}
                    onDimensionsChange={handleProfileDimensionsChange}
                    onProfileChange={handleProfileChange}
                />
            ) : null}

            <ThicknessAndSheetControls
                isMaterialUnavailable={isMaterialUnavailable}
                isPartFromLibrary={isPartFromLibrary}
                isRotaryPart={Boolean(isRotaryPart)}
                locale={locale}
                sheetDisabled={!quoteItem?.thickness || !sheetOptions?.length || isCalculatingQuote}
                sheetOptions={sheetOptions}
                sheetValue={quoteItem?.sheetId || ''}
                thicknessDisabled={
                    !quoteItem?.materialId ||
                    !thicknessOptions?.length ||
                    (mode === 'rotary' &&
                        showTubeOptions &&
                        (!(quoteItemSheetProfile || quoteItem?.profile) ||
                            !(quoteItemSheetDimensions || quoteItemDimensions))) ||
                    isPartFromLibrary ||
                    isCalculatingQuote
                }
                thicknessOptions={thicknessOptions}
                thicknessValue={thicknessSelectValue}
                units={units}
                useImperialUnits={useImperialUnits}
                onSheetChange={handleSheetChange}
                onThicknessChange={handleThicknessChange}
            />

            {showGrainDirection && !isRotaryPart ? (
                <GrainDirectionSelector
                    disabled={isPartFromLibrary || isCalculatingQuote}
                    isPartFromLibrary={isPartFromLibrary}
                    options={grainDirectionOptions}
                    value={quoteItem?.grainDirection || 'None'}
                    onChange={handleGrainDirectionChange}
                />
            ) : null}

            <ConsumptionModeSelector
                disabled={!materialConsumptionModeEnabled || isCalculatingQuote}
                options={consumptionModeOptions}
                value={quoteItem?.materialConsumptionMode || ''}
                onChange={handleConsumptionModeChange}
            />

            <CustomerSuppliedMaterialToggle
                checked={quoteItem?.isCustomerSuppliedMaterial || false}
                disabled={!orgHasCustomerSuppliedMaterial || !selectedSheet?.isSoldByOrganisation || isCalculatingQuote}
                orgHasFeature={orgHasCustomerSuppliedMaterial}
                selectedSheet={selectedSheet}
                onChange={handleIsCustomerSuppliedMaterialChange}
            />

            {openNotesModal ? (
                <ItemNotesModal
                    handleUpdate={handleUpdateQuoteItem}
                    open={openNotesModal}
                    quoteItem={quoteItem}
                    onClose={hideNotesModal}
                />
            ) : null}
        </Box>
    )
}

ItemSettings.propTypes = {
    quoteItemId: PropTypes.string,
}

export default memo(ItemSettings)
